import type { GlobalPatientClinicLink, GlobalPatientMatch, GlobalPatientSearchResponse } from '../types';

/**
 * The 10-digit Indian mobile in a search box, or null when it doesn't hold a complete one.
 * Accepts "+91" / "91" prefixes and spaces or dashes between digits ("+91 98765 43210").
 * Anything else (a name, a partial number, a UHID) is not a mobile.
 */
export function fullIndianMobile(input: string | null | undefined): string | null {
  const compact = String(input ?? '').replace(/[\s-]/g, '');
  const m = /^(?:\+?91)?(\d{10})$/.exec(compact);
  return m ? m[1] : null;
}

/**
 * The people a global search found (medopd-docs 122). Reads `matches[]`; an older backend that only
 * sends the legacy single-result fields is read as one match. Never invents data the response lacks.
 */
export function globalMatchesOf(res: GlobalPatientSearchResponse | null | undefined): GlobalPatientMatch[] {
  if (!res?.found) return [];
  if (Array.isArray(res.matches)) {
    return res.matches.filter((m) => !!m?.globalPatientId && (m.clinicLinks?.length ?? 0) > 0);
  }
  if (!res.globalPatientId || !res.clinicLinks?.length) return [];
  return [{
    globalPatientId: res.globalPatientId,
    maskedName: res.maskedName ?? '',
    maskedMobile: res.maskedMobile ?? '',
    gender: res.gender ?? null,
    clinicLinks: res.clinicLinks,
  }];
}

/** Links newest visit first; links with no known visit last, in their original order. */
export function byMostRecentVisit(links: GlobalPatientClinicLink[]): GlobalPatientClinicLink[] {
  const time = (l: GlobalPatientClinicLink) => {
    const t = l.lastVisit ? Date.parse(l.lastVisit) : NaN;
    return isNaN(t) ? -Infinity : t;
  };
  return links.map((l, i) => ({ l, i }))
    .sort((a, b) => (time(b.l) - time(a.l)) || (a.i - b.i))
    .map(({ l }) => l);
}

/** Distinct clinics across all matches — the "n other clinics" in the registration notice. */
export function distinctClinicCount(matches: GlobalPatientMatch[]): number {
  return new Set(matches.flatMap((m) => m.clinicLinks.map((l) => l.clinicId))).size;
}
